/**
 * The baked SFX catalog: `data/baked/manifest.json`'s 34 `category: "sfx"` entries, one row per
 * `.ogg`, with an **INFERRED** guess at what each cue is for.
 *
 * **Rebaked (2026-08-19, AdLib SFX lane).** Every entry here used to describe the *old*
 * MT-32-soundfont render, which `docs/engineering/lessons.md`'s "AdLib SFX lane" entry and
 * `docs/formats/sound-music.md`'s "Rendering the AdLib tracks" section both retired: that render
 * measured spectral flatness 0.0000 on all 34 cues (pure synth tones — no gun, no engine). The
 * current `data/baked/sfx/*.ogg` files are a full re-render through an emulated OPL2 loaded with
 * the game's own `003:` instrument bank (`device: "adlib-custom-bank"` in the manifest), which
 * measures broadband/noisy for the impact family and stays periodic for the drones — see the
 * table below. Every duration/channel/note-count/loop field in this file is copied from that
 * rebake; nothing here reads the manifest at runtime, same policy as before.
 *
 * **Six roles are no longer guesses (2026-08-21, GUN AUDIO lane).** The first live OPL2 capture
 * taken with the guns firing (`docs/formats/sound-music.md` § "Gunfire, jams and hits", session
 * `re-sessions/gun-audio/`) reads the cue numbers straight off the register stream:
 * **SIMSNDS-01 = the player's guns** (one crack per round, 8.47/s), **SIMSNDS-04 = the enemy's
 * guns** (same cadence, range-attenuated), **SIMSNDS-11 = the dead-trigger click** (jam *and*
 * empty belt), **SIMSNDS-12 = the `U` clear-jam action**, **SIMSNDS-13 = rounds striking you**,
 * and **SIMSNDS-16 was never played at all**. Those six entries below are VERIFIED; three
 * displaced INFERRED roles moved onto the slots they freed, and say so.
 *
 * There is still no name/label anywhere in the source data — SCI sound resources are numbered, not
 * named — so every *remaining* `role` string below is a guess, defensible from four kinds of
 * evidence and no more:
 *
 * 1. **Measured spectral tonality/centroid**, for the four cues `sound-music.md` ran the FFT on —
 *    read as bare cue numbers, since the role words that labelled them are all retired now: cue 2
 *    (0.722 / 3019 Hz — the engine), cue 16 (0.309 / 7125 Hz, the noisiest in the pool — and never
 *    played), cue 25 (0.728 / 2709 Hz — wind), cue 0 (0.994 / 2870 Hz — never played either).
 *    Spectral shape places a cue's *character*; only a live capture places its *role*.
 * 2. **The SCI loop-marker census** (`sound-music.md`'s "Ten cues are gate-style, not one-shots"):
 *    resources 2, 3, 10, 19, 21, 23, 24, 26, 27, 28 carry the `0xCF 0x7F` set-loop-point marker on
 *    control channel 15 *and* end their script with a note still held. `LOOP_MARKED_SIMSNDS_IDS`
 *    pins the list; `sustainedAtEnd` must match it exactly (`sfx-map.test.ts` checks that).
 * 3. **Rendered length vs. scripted length** — `durationS` is the `.SNG` script's own span,
 *    `renderedSeconds` what got baked; for a `sustainedAtEnd` cue the gap is the held window
 *    `audio-plan.md` §3 grants it, for the rest an ordinary release tail. Duration *alone* is a
 *    weak signal (the old `GUNFIRE_TICK_FAMILY` grouping did not survive the rebake), so this file
 *    no longer groups cues by scripted duration.
 * 4. **Byte-identical duplication with `SHLSNDS`/`SHLSNDS2`** — four entries share a `midSha256`
 *    with a shell blip. That used to be read as "so it is a notification ding"; the gun capture
 *    shows the inference is weaker than it looked (SIMSNDS-01 is byte-identical to a menu blip
 *    *and* is the machine guns), so it now argues only "short and cheap", not "harmless".
 *
 * Every cue this port wires to a trigger is documented in `sfx-map.ts`, which imports `SIMSNDS_ID`s
 * from here — that is the single source of truth for "which id means what"; this file carries the
 * per-id evidence. Cues with no wired role are still listed with a structural guess so the gaps
 * stay visible. That pass left SIMSNDS-04 as the only deliberately unwired entry; G36 wired it on
 * 2026-08-27, so every cue with a recovered role now has a trigger.
 *
 * **This module is the data and the evidence.** `simsnds-cues.ts` next door derives the public
 * surface from it (`SfxCueId`, `SIMSNDS_CUES`, `SIMSNDS_CUE_IDS`, `cuePath`,
 * `LOOP_MARKED_SIMSNDS_IDS`); every consumer imports from there, not from here. The split is
 * mechanical — the catalog outgrew the project's 400-line file gate once six roles stopped being
 * one-line guesses — and nothing but `RAW_CUES` and its types moved.
 */

export interface SfxCueMeta {
  /** `/baked/<path>` — ready to `fetch()`/assign as an `<audio>` `src`. */
  readonly path: string;
  /** Length of the source `.SNG` event stream in seconds — what the script itself spans. */
  readonly durationS: number;
  /** Length of the actual baked audio — exceeds `durationS` by a release tail, or (for a `sustainedAtEnd` cue) by the held window `audio-plan.md` §3 grants it. */
  readonly renderedSeconds: number;
  /** True for exactly the ten ids in `LOOP_MARKED_SIMSNDS_IDS` — SCI loop-marked, gate-style, ends the script with a note still held. */
  readonly sustainedAtEnd: boolean;
  readonly channels: number;
  readonly noteCount: number;
  /** Another cue id with the exact same rendered bytes (`midSha256`), if any. */
  readonly duplicateOf?: string;
  /**
   * MEASURED loop region overriding the generic rules — set only where the cue's actual RMS
   * envelope was measured and the generic region is wrong (see `loopRegionFor`).
   */
  readonly loopRegionOverrideS?: { readonly startS: number; readonly endS: number };
  /** INFERRED — see the file header for the evidence this is built from. */
  readonly role: string;
}

/** The measured facts a `cue()` call carries — split from `role`/`duplicateOf` so the factory stays under the project's param-count bar. */
interface CueFacts {
  readonly durationS: number;
  readonly renderedSeconds: number;
  readonly sustainedAtEnd: boolean;
  readonly channels: number;
  readonly noteCount: number;
  readonly loopRegionOverrideS?: { readonly startS: number; readonly endS: number };
}

/** One catalog row — every `RAW_CUES` entry is a call to this, so the 34 near-identical shapes below are argument lists, not repeated object literals (`docs/engineering/lessons.md`'s "in-flight audio lane" entry — a data catalog written as N structurally-identical object literals trips `code-quality/duplicate-block`; a factory call per entry does not). */
function cue(fileName: string, facts: CueFacts, role: string, duplicateOf?: string): SfxCueMeta {
  const path = `/baked/sfx/${fileName}`;
  return duplicateOf === undefined ? { path, ...facts, role } : { path, ...facts, role, duplicateOf };
}

/**
 * `docs/formats/sound-music.md` counts **31** `SIMSNDS.SNG` resources; the bake manifest carries
 * only indices 0–29 (30 files). Not this lane's bake pipeline to fix (data/baked is read-only
 * here) — noted so the gap isn't mistaken for a mapping bug. `SIMSNDS-30`'s absence just means one
 * fewer id in the pool below.


/**
 * All 34 baked `sfx/*` entries. Durations/rendered-lengths/channels/notes/loop flags are copied
 * verbatim from `data/baked/manifest.json`; nothing here reads the manifest at runtime (it stays a
 * build-time asset list, same as `title-menu.ts`'s hardcoded baked paths).
 */
export const RAW_CUES = {
  "SHLSNDS-00": cue(
    "SHLSNDS-00.ogg",
    { durationS: 0.133, renderedSeconds: 0.138, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "Shell/menu UI blip (not `/fly` gameplay). Byte-identical to SIMSNDS-14 — see that entry.",
  ),
  "SHLSNDS-01": cue(
    "SHLSNDS-01.ogg",
    { durationS: 0.15, renderedSeconds: 0.16, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "Shell/menu UI blip. Byte-identical to SIMSNDS-15 — see that entry.",
  ),
  "SHLSNDS-02": cue(
    "SHLSNDS-02.ogg",
    { durationS: 0.067, renderedSeconds: 0.077, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "Shell/menu UI blip. Byte-identical to SIMSNDS-29 — see that entry.",
  ),
  SHLSNDS2: cue(
    "SHLSNDS2.ogg",
    { durationS: 0.1, renderedSeconds: 0.11, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "Shell/menu UI blip. Byte-identical to SIMSNDS-01 — see that entry.",
  ),

  "SIMSNDS-00": cue(
    "SIMSNDS-00.ogg",
    { durationS: 4.4, renderedSeconds: 4.405, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "UNWIRED — MEASURED never-played. Four whole captured flights (sound-music.md § 'Continuous " +
      "engine sound') show cue 0 is not played at all: the catalogue's 'engine drone' label was " +
      "wrong, and so was this port's follow-on 'engine catches' accent, now removed. Its own " +
      "measurements stand (tonality 0.994 / centroid 2870 Hz, a dense harmonic buzz on a 1174 Hz " +
      "fundamental) — they just describe nothing the game emits. Kept in the pool because 'baked, " +
      "measured, demonstrably unused' is a finding worth keeping visible.",
  ),
  "SIMSNDS-01": cue(
    "SIMSNDS-01.ogg",
    { durationS: 0.1, renderedSeconds: 0.11, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "**THE PLAYER'S MACHINE GUNS** — VERIFIED by live OPL capture, not inferred (sound-music.md " +
      "§ 'Gunfire, jams and hits'). Bank program 3, note 14 → fnum 385 / block 0, one voice, keyed " +
      "on 101 ms and off 17 ms **per round**, period 118 ms = 8.47 rounds/s, identical on the " +
      "twin-gun Camel and the single-gun S.E.5a. Feedback-7 FM, sustain level 15 both operators: an " +
      "instant broadband crack gone in ~75 ms. The baked render is the real thing — replaying the " +
      "captured registers through packages/baker's OPL2 correlates 0.959 with this file, within " +
      "0.7 dB in every octave band — so no rebake is needed. Its byte-identity with a shell ding is " +
      "genuine (the original reuses one crack for both), which is what made the old 'carburettor " +
      "thawed blip' reading look plausible; that role moved to SIMSNDS-29.",
    "SHLSNDS2",
  ),
  "SIMSNDS-02": cue(
    "SIMSNDS-02.ogg",
    { durationS: 0.033, renderedSeconds: 0.231, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "**THE PLAYER'S ENGINE** — VERIFIED by live OPL capture, not inferred. First sound the sim " +
      "starts, keyed on once and never released, the only voice whose pitch tracks the throttle: " +
      "note 24 / velocity 64 on bank program 4, the only resource in the game that sends it " +
      "(sound-music.md § 'Continuous engine sound'). The old 'gun burst' label and the " +
      "`GUN_LOOP_CUE` held-fire loop built on it were both wrong; the measured broadband spectrum " +
      "(0.722 / 3019 Hz) is a 24-55 Hz FM growl transposed up to its authored note. **This baked " +
      ".ogg is not played by the port at all** — a fixed render cannot do the ~10 s 57-step pitch " +
      "glide that defines the sound, so `engine-voice.ts` runs the patch live on a real OPL2 voice " +
      "in an AudioWorklet (audio-plan.md §6).",
  ),
  "SIMSNDS-03": cue(
    "SIMSNDS-03.ogg",
    { durationS: 0.033, renderedSeconds: 0.231, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "Structurally identical to SIMSNDS-02 (same script, render, channel/note shape and loop marker) " +
      "— almost certainly the same broadband noise-burst patch family. INFERRED `roundsOnTarget`: a " +
      "one-shot tick, not looped, despite being gate-style. Not observed in the gun capture (the " +
      "player never landed a hit), so this one is still a guess.",
  ),
  "SIMSNDS-04": cue(
    "SIMSNDS-04.ogg",
    { durationS: 0.1, renderedSeconds: 0.11, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "**THE ENEMY'S MACHINE GUNS** — VERIFIED, captured while von Richthofen shot the player down: " +
      "bank program 7, note 12 → fnum 343 / block 0, same 118 ms / 101 ms cadence as the player's " +
      "cue 1 but with a **carrier total level that tracks range** (9 → 18 as the Fokker fell back), " +
      "the same distance attenuation the enemy-engine voices use. The old 'bullet-strike tick " +
      "(takingHits)' reading was close in spirit and wrong in fact: strikes are SIMSNDS-13. WIRED " +
      "2026-08-27 (G36), after six days reserved-but-unplayed for want of an 'another aeroplane is " +
      "firing' signal: `encounter-views.ts`'s `AircraftView.firingNow` is that signal, and " +
      "`enemy-gun-audio.ts` plays it through `sfx-map.ts`'s `ENEMY_GUN_CUE`.",
  ),
  "SIMSNDS-05": cue(
    "SIMSNDS-05.ogg",
    { durationS: 1.967, renderedSeconds: 1.977, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED spiralEntered cue — a long single tone for a serious, escalating flight-mode warning.",
  ),
  "SIMSNDS-06": cue(
    "SIMSNDS-06.ogg",
    { durationS: 1.5, renderedSeconds: 1.505, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED terminal-impact tone, reused for `crashed` (own) and `destroyed` (enemy, 'ENEMY " +
      "DESTROYED') — both are the same kind of event ('something just blew up/hit the ground') seen " +
      "from different cockpits.",
  ),
  "SIMSNDS-07": cue(
    "SIMSNDS-07.ogg",
    { durationS: 1.75, renderedSeconds: 1.812, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED descending-tone cue, reused for `hypoxiaBlackout` (own) and `outOfControl` (enemy, " +
      "'HE'S GOING DOWN!') — both read as 'things are going wrong, and down'.",
  ),
  "SIMSNDS-08": cue(
    "SIMSNDS-08.ogg",
    { durationS: 2.95, renderedSeconds: 2.955, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED wingFailure cue — the longest mid-tier tone in the pool, for the single most dramatic " +
      "mid-air failure.",
  ),
  "SIMSNDS-09": cue(
    "SIMSNDS-09.ogg",
    { durationS: 1.4, renderedSeconds: 1.539, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED terminal/solemn tone, reused for `pilotDied` and `encounterLost` ('YOU'VE BEEN SHOT " +
      "DOWN') — both are 'this flight is over, and you lost' signals.",
  ),
  "SIMSNDS-10": cue(
    "SIMSNDS-10.ogg",
    { durationS: 0.033, renderedSeconds: 3.14, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "CORRECTED — not a gun. Loop-marked/gate-style, but its render takes 3.14 s to finish attacking " +
      "(a slow swell, not an instant hit); the old catalog wired it as a second gunfire variant on a " +
      "shared 0.033 s script length, which says nothing about the instrument. INFERRED " +
      "`gunJamPermanent`: a slow rising mechanical swell reads as guns seizing for good. The gun " +
      "capture neither confirms nor refutes this — no permanent jam was reached.",
  ),
  "SIMSNDS-11": cue(
    "SIMSNDS-11.ogg",
    { durationS: 0.183, renderedSeconds: 0.225, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "**THE DEAD-TRIGGER CLICK** — VERIFIED. What the original plays while the trigger is held and " +
      "nothing comes out, for **both** dead-gun states: bank program 14, note 12 → fnum 343 / " +
      "block 0, 186 ms notes started ~56 ms apart in clusters of **4** when jammed (period 439.5 ms) " +
      "and **3** when empty (period 385.8 ms), on up to four voices at once. No separate jam-onset " +
      "sting exists — the first cluster lands on the same frame as the HUD line and keeps going as " +
      "long as you keep pulling. Its old 'pilotWounded' role moved to SIMSNDS-16.",
  ),
  "SIMSNDS-12": cue(
    "SIMSNDS-12.ogg",
    { durationS: 0.317, renderedSeconds: 0.322, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "**THE CLEAR-JAM ACTION (`U`)** — VERIFIED. One 320 ms note, bank program 15, fnum 514 / " +
      "block 1, total level 4/4, on the frame `U` is pressed. In the captured attempt the jam did " +
      "**not** clear afterwards, so this is the *attempt*, not a confirmed 'cleared' — the one " +
      "gun-audio question the session left open. Wired to `gunCleared` because that is the only " +
      "clear-jam hook the audio layer is given. Its old 'gunJammed' role moved to SIMSNDS-11, which " +
      "is what a jam actually sounds like.",
  ),
  "SIMSNDS-13": cue(
    "SIMSNDS-13.ogg",
    { durationS: 0.217, renderedSeconds: 0.608, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "**ROUNDS STRIKING YOUR AEROPLANE** — VERIFIED. Bank program 16, fnum 514 / block 0, 219 ms " +
      "notes on their own voices at 490-710 ms intervals, captured through the burst that put " +
      "'Your fuel lines have been hit!' on the HUD. Wired to `takingHits` and, following it, the " +
      "flak-hit line. `bleedOutWarning` — its old role — now shares it: both mean 'you are being " +
      "damaged', and the shipped pool has no spare left after this lane's reassignments. INFERRED " +
      "for that second use, VERIFIED for the first.",
  ),
  "SIMSNDS-14": cue(
    "SIMSNDS-14.ogg",
    { durationS: 0.133, renderedSeconds: 0.138, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED stallRecovered cue. Byte-identical to a shell-menu ding — reused notification audio " +
      "for 'condition cleared'.",
    "SHLSNDS-00",
  ),
  "SIMSNDS-15": cue(
    "SIMSNDS-15.ogg",
    { durationS: 0.15, renderedSeconds: 0.16, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED spinRecovered cue. Byte-identical to a shell-menu ding, same 'condition cleared' " +
      "family as SIMSNDS-14.",
    "SHLSNDS-01",
  ),
  "SIMSNDS-16": cue(
    "SIMSNDS-16.ogg",
    { durationS: 0.383, renderedSeconds: 0.785, sustainedAtEnd: false, channels: 2, noteCount: 9 },
    "CORRECTED — **not gunfire, and MEASURED never-played.** Five captured flights (guns firing, " +
      "jammed, empty, the clear-jam key, and being shot down) contain **zero** key-ons on this " +
      "cue's bank program 23. Gunfire is SIMSNDS-01, one crack per round; this 0.785 s nine-note " +
      "rattle every 0.383 s was this port's own invention and the defect the user reported " +
      "('the machine gun sounds are definitely off') — tonality 0.049 / centroid 9440 Hz against " +
      "the original's 0.886 / 725 Hz. Its spectral measurements still stand, they just describe a " +
      "sound the game never emits. Re-roled to `pilotWounded`, displaced off SIMSNDS-11 by the " +
      "dead-trigger click — INFERRED: a wound announcement can afford a long distinctive cue and " +
      "must not sound like a jammed trigger.",
  ),
  "SIMSNDS-17": cue(
    "SIMSNDS-17.ogg",
    { durationS: 1.0, renderedSeconds: 1.252, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED groundContact cue — a mid-weight thump, lighter than the `crashed` cue.",
  ),
  "SIMSNDS-18": cue(
    "SIMSNDS-18.ogg",
    { durationS: 1.9, renderedSeconds: 1.926, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED spinEntered cue — long single tone for a serious flight-mode warning, distinct id " +
      "from spiralEntered's SIMSNDS-05.",
  ),
  "SIMSNDS-19": cue(
    "SIMSNDS-19.ogg",
    { durationS: 0.017, renderedSeconds: 0.127, sustainedAtEnd: true, channels: 1, noteCount: 1 },
    "One of the ten loop-marked/gate-style cues. Wired as `FIRE_LOOP_CUE`: the continuous 'burn' " +
      "bed, looped from its steady tail and gain-ramped by whether the aeroplane is burning — the " +
      "loop-from-loop-point treatment audio-plan.md §3 calls for, applied to a real continuous game " +
      "state instead of left as a bare retrigger tick.",
  ),
  "SIMSNDS-20": cue(
    "SIMSNDS-20.ogg",
    { durationS: 0.667, renderedSeconds: 0.867, sustainedAtEnd: false, channels: 1, noteCount: 4 },
    "INFERRED stall-warning buzzer, reused for the 'YOU'RE DANGEROUSLY LOW.' altitude warning. Not " +
      "the only multi-note cue (SIMSNDS-16 has 9, SIMSNDS-24 has 2) but the only one whose patch " +
      "reads as a fast repeating trill (4 notes in 0.667 s) — the best-fitting buzzer shape.",
  ),
  "SIMSNDS-21": cue(
    "SIMSNDS-21.ogg",
    { durationS: 0.017, renderedSeconds: 0.153, sustainedAtEnd: true, channels: 1, noteCount: 1 },
    "INFERRED hypoxiaRecovered cue ('YOU'RE COMING AROUND.') — loop-marked/gate-style like the other " +
      "nine, but used as a one-shot here (a 'condition cleared' ding is a moment, not a held state).",
  ),
  "SIMSNDS-22": cue(
    "SIMSNDS-22.ogg",
    { durationS: 0.567, renderedSeconds: 0.572, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED engine-cough tone, reused for `fuelExhausted`, `engineSputtering` ('YOUR ENGINE IS " +
      "SPUTTERING!') and the derived 'YOU'RE LOW ON FUEL.' warning — all three are the same " +
      "underlying idea (the engine/fuel system is complaining).",
  ),
  "SIMSNDS-23": cue(
    "SIMSNDS-23.ogg",
    { durationS: 0.033, renderedSeconds: 0.231, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "Loop-marked; a retriggered `FIRE_CRACKLE_POP_CUES` variant (with SIMSNDS-26) layered over " +
      "`FIRE_LOOP_CUE`'s bed for irregular pop-pop-pop texture — crackling fire is bursty, which " +
      "retriggering renders better than a second smooth loop.",
  ),
  "SIMSNDS-24": cue(
    "SIMSNDS-24.ogg",
    { durationS: 0.3, renderedSeconds: 0.73, sustainedAtEnd: true, channels: 1, noteCount: 2 },
    "Previously an undocumented 'spare', grouped into the old duration-only tick family on a " +
      "pre-rebake 0.033 s guess the real render (0.300 s script, 2 notes, 0.730 s) never matched. " +
      "Loop-marked/gate-style; claimed as `STALL_RUSH_LOOP_CUE`, a continuous buffet layer gated on " +
      "`SimState.readout.stalled`.",
  ),
  "SIMSNDS-25": cue(
    "SIMSNDS-25.ogg",
    {
      durationS: 10.45,
      renderedSeconds: 10.455,
      sustainedAtEnd: false,
      channels: 1,
      noteCount: 1,
      // MEASURED (offline probe, 2026-08-19): RMS envelope is silent to ~1 s, swells to a -13 dB
      // peak near 3.5 s, then decays ~2.4 dB/s. The generic decaying rule looped [0.15, 10.45] --
      // a ~10 s loud/fade "breathing" wall. This region loops the -13..-15 dB plateau only.
      loopRegionOverrideS: { startS: 3.2, endS: 4.6 },
    },
    "MEASURED — tonality 0.728 / centroid 2709 Hz, broadband (sound-music.md), the longest cue in " +
      "the pool. VERIFIED NOT one of the ten SCI loop-marked cues despite being the obvious " +
      "'continuous' candidate: renderedSeconds exceeds durationS by only 5 ms, so it is a long " +
      "natural note, not a gate-style resource. Still wired as `WIND_LOOP_CUE` — audio-plan.md's own " +
      "recommendation is to loop wind as a modernized port choice regardless — with a loop region " +
      "that trims the attack guard and release tail (`decayingLoopRegionS`) rather than the " +
      "tail-fraction rule the ten gate cues use.",
  ),
  "SIMSNDS-26": cue(
    "SIMSNDS-26.ogg",
    { durationS: 0.033, renderedSeconds: 0.592, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "Loop-marked; second `FIRE_CRACKLE_POP_CUES` variant, alternated with SIMSNDS-23.",
  ),
  "SIMSNDS-27": cue(
    "SIMSNDS-27.ogg",
    { durationS: 0.033, renderedSeconds: 0.231, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "INFERRED hypoxiaDizzy cue — a subtle short warble standing in for disorientation onset. Same " +
      "loop-marked 0.231 s shape as SIMSNDS-02/03/23, used here as a one-shot rather than looped.",
  ),
  "SIMSNDS-28": cue(
    "SIMSNDS-28.ogg",
    { durationS: 0.033, renderedSeconds: 0.159, sustainedAtEnd: true, channels: 2, noteCount: 1 },
    "INFERRED carburettorIced cue — a quick metallic tick for icing onset. Loop-marked/gate-style " +
      "but used as a one-shot: icing onset is a momentary ping, not a held state.",
  ),
  "SIMSNDS-29": cue(
    "SIMSNDS-29.ogg",
    { durationS: 0.067, renderedSeconds: 0.077, sustainedAtEnd: false, channels: 1, noteCount: 1 },
    "INFERRED carburettorThawed cue. Byte-identical to a shell-menu ding — same 'condition cleared' " +
      "notification family as SIMSNDS-14/15, which is exactly the character SIMSNDS-01 was credited " +
      "with before the capture proved it to be gunfire, so this role lands here on the slot " +
      "SIMSNDS-12 (the VERIFIED clear-jam cue) freed. Its own former 'gunCleared' role went with it.",
    "SHLSNDS-02",
  ),
} as const satisfies Record<string, SfxCueMeta>;
